import { useState } from 'react'
import { ExternalLink, Calendar, User, Bug } from 'lucide-react'
import { useArticles } from '../hooks/useArticles'
import type { Article } from '../types/blog'

/* ─────────────────────────────────────────
   Helpers
   ───────────────────────────────────────── */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function sourceAbbr(name: string): string {
  return name
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 4)
}

const BADGE_CLS: Record<string, string> = {
  critical: 'badge-critical text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5',
  high:     'badge-high     text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5',
  medium:   'badge-medium   text-white font-mono text-[10px] uppercase tracking-widest px-2 py-0.5',
  low:      'badge-low      font-mono text-[10px] uppercase tracking-widest px-2 py-0.5',
}

const ACCENT_COLOR: Record<string, string> = {
  critical: '#DC143C',
  high:     '#cc7700',
  medium:   '#556B2F',
  low:      '#2a2a2a',
}

type FilterLevel = 'all' | 'critical' | 'high' | 'medium'

const FILTER_TABS: { label: string; value: FilterLevel }[] = [
  { label: 'ALL', value: 'all' },
  { label: 'CRITICAL', value: 'critical' },
  { label: 'HIGH', value: 'high' },
  { label: 'MEDIUM', value: 'medium' },
]

/* ─────────────────────────────────────────
   Page
   ───────────────────────────────────────── */
export default function Exploits() {
  const [filter, setFilter] = useState<FilterLevel>('all')

  const { articles, total, loading, error } = useArticles({
    category: 'vulnerability',
    threat_level: filter === 'all' ? undefined : filter,
    limit: 30,
  })

  return (
    <div className="bg-terminal-bg min-h-screen">
      <div className="container mx-auto px-4 max-w-7xl py-10">

        {/* ── Header ── */}
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-bear mb-2">
            ★ &nbsp; Vulnerability Research Division
          </p>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-white uppercase tracking-wider mb-3">
            Exploit Database
          </h1>
          <p className="text-text-gray font-mono text-sm leading-relaxed max-w-2xl">
            CVEs, zero-days, and vulnerability disclosures — analyzed and classified.
            Raw intelligence from the front lines of offensive security research.
          </p>
        </div>

        {/* ── Section divider ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-border-gray/50" />
          <div className="flex items-center gap-2">
            <Bug className="text-bear" size={16} />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-text-gray">
              {loading ? 'QUERYING DATABASE...' : `${total} VULNERABILITY RECORDS`}
            </span>
          </div>
          <div className="h-px flex-1 bg-border-gray/50" />
        </div>

        {/* ── Filter tabs ── */}
        <div className="flex gap-0 mb-8 border border-border-gray w-fit">
          {FILTER_TABS.map(({ label, value }, i) => (
            <button
              key={value}
              onClick={() => setFilter(value)}
              className={`font-mono text-[10px] uppercase tracking-widest px-5 py-2.5 transition-all cursor-pointer ${
                i > 0 ? 'border-l border-border-gray' : ''
              } ${
                filter === value
                  ? 'bg-kgb-red text-white'
                  : 'bg-transparent text-text-gray hover:bg-panel-bg hover:text-text-light'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="terminal-window p-6">
            <p className="font-mono text-status-secure text-sm animate-pulse">
              $ SCANNING INTEL FEEDS...<span className="animate-blink">_</span>
            </p>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="terminal-window p-6">
            <p className="font-mono text-kgb-red text-sm">[ERROR] {error}</p>
          </div>
        )}

        {/* ── List ── */}
        {!loading && !error && (
          <div className="flex flex-col gap-0">
            {articles.length === 0 ? (
              <div className="terminal-window p-6 text-center">
                <p className="font-mono text-text-gray text-sm">
                  [INFO] No vulnerability records match the current filter.
                </p>
              </div>
            ) : (
              articles.map(article => (
                <ExploitRow key={article.id} article={article} />
              ))
            )}
          </div>
        )}

      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   Exploit Row
   ───────────────────────────────────────── */
function ExploitRow({ article }: { article: Article }) {
  const accent = ACCENT_COLOR[article.threat_level] ?? '#2a2a2a'

  return (
    <div
      className="bracket-card bg-panel-bg border border-border-gray hover:border-kgb-red/40 transition-all group"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="p-4 flex items-start gap-4">

        {/* Threat badge column */}
        <div className="flex-shrink-0 pt-0.5">
          <span className={BADGE_CLS[article.threat_level]}>
            {article.threat_level}
          </span>
        </div>

        {/* Source abbreviation box */}
        <div
          className="flex-shrink-0 w-10 h-10 flex items-center justify-center border border-border-gray bg-black/60 font-mono text-[9px] font-bold text-text-gray uppercase"
          title={article.source_name}
        >
          {sourceAbbr(article.source_name)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-white text-base leading-snug mb-1 group-hover:text-kgb-red transition-colors truncate">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="font-mono text-xs text-text-gray leading-relaxed line-clamp-2 mb-2">
              {article.excerpt}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-text-gray/60">
            {article.author && (
              <span className="flex items-center gap-1">
                <User size={10} />
                {article.author}
              </span>
            )}
            <span className="uppercase tracking-wide">{article.source_name}</span>
            <span className="flex items-center gap-1">
              <Calendar size={10} />
              {formatDate(article.published_at)}
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0 flex items-center">
          <a
            href={article.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-kgb-red/60 hover:text-kgb-red transition-colors border border-kgb-red/30 hover:border-kgb-red px-3 py-1.5 whitespace-nowrap"
          >
            VIEW EXPLOIT
            <ExternalLink size={9} />
          </a>
        </div>
      </div>
    </div>
  )
}
